import React, { useState } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { Eye, EyeSlash, WarningCircle } from '@phosphor-icons/react';
import { useInputId } from './hooks';

const FormPassword = React.forwardRef(
  (
    {
      id: defaultId,
      label,
      name,
      onChange,
      onSetFieldValue,
      value,
      error,
      required,
      noAutoFill,
      ...props
    },
    ref,
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const [id] = useInputId(defaultId);
    const handleChange = ({ target }) => {
      if (typeof onChange === 'function') {
        onChange((state) => ({
          ...state,
          [name]: target.value,
        }));
      }
      if (typeof onSetFieldValue === 'function') {
        onSetFieldValue(name, target.value);
      }
    };

    return (
      <div className="space-y-1.5">
        {label && (
          <label
            htmlFor={id}
            className="block text-xs font-semibold text-neutral-700"
          >
            <span>{label}</span>
            {required && <span className="ml-1 text-danger-600">*</span>}
          </label>
        )}
        <div
          className={cn(
            'relative h-9 flex items-center border rounded px-3 z-[1]',
            'focus-within:ring-2 focus-within:ring-offset-2',
            error
              ? 'border-danger-600 bg-danger-50 focus-within:ring-danger-600 animate-wiggle'
              : 'border-neutral-300 bg-white focus-within:border-brand-600 focus-within:ring-brand-600',
          )}
        >
          <input
            id={id}
            ref={ref}
            name="name"
            className="block w-full border-0 p-0 pr-8 bg-transparent focus:ring-0 text-sm text-neutral-900 placeholder:text-neutral-500 focus:outline-none"
            onChange={handleChange}
            value={value || ''}
            {...props}
            type={showPassword ? 'text' : 'password'}
            autoComplete={noAutoFill ? 'new-password' : 'on'}
          />

          {error ? (
            <div
              className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
              title={error || ''}
            >
              <WarningCircle
                size={16}
                className="text-danger-600"
                aria-hidden="true"
              />
            </div>
          ) : (
            <button
              type="button"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-neutral-500 hover:text-neutral-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 rounded"
              onClick={() => setShowPassword((val) => !val)}
              tabIndex="-1"
            >
              {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
            </button>
          )}
        </div>
      </div>
    );
  },
);

FormPassword.defaultProps = {
  label: '',
  id: '',
  type: 'text',
  error: null,
  required: false,
  onChange: false,
  onSetFieldValue: false,
  noAutoFill: false,
};

FormPassword.propTypes = {
  id: PropTypes.string,
  type: PropTypes.string,
  required: PropTypes.bool,
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  error: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
  onChange: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  onSetFieldValue: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  noAutoFill: PropTypes.bool,
};

export default React.memo(FormPassword);
